import { inject, Injectable } from '@angular/core';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { DayjsLocaleService } from '@app/core/i18n/services/dayjs-locale.service';
import { IDateRange } from '@app/core/date-range/interfaces/date-range.interface';

@Injectable({
  providedIn: 'root',
})
export class DateRangeComparisonService {
  private dayjsLocale = inject(DayjsLocaleService);

  /**
   *
   * @description Convert date from BE to day start in to the ms
   * @params dateStr: '2025-10-26'
   */
  public getTimestamp(dateStr: string): number {
    return this.dayjsLocale.dayjs(dateStr).startOf('day').valueOf();
  }

  /**
   *
   * @description base range include target range
   * time period:
   *
   * `---(baseStart--(targetStart-------targetEnd)---baseEnd)--->`
   */
  public isRangeIntersecting(base: IDateRange, target: IDateRange): boolean {
    const baseStart = base.startDate ? this.getTimestamp(base.startDate) : -Infinity;
    const baseEnd = base.endDate ? this.getTimestamp(base.endDate) : Infinity;

    const targetStart = target.startDate ? this.getTimestamp(target.startDate) : -Infinity;
    const targetEnd = target.endDate ? this.getTimestamp(target.endDate) : Infinity;

    return baseStart <= targetStart && targetEnd <= baseEnd;
  }

  /**
   *
   * @description target range out of base range
   * time period:
   *
   * `---(targetStart---targetEnd)---(baseStart-------baseEnd)--->`
   *
   * or
   *
   * `---(baseStart-------baseEnd)---(targetStart---targetEnd)--->`
   */
  public isBaseRangeInvalid(base: IDateRange, target: IDateRange): boolean {
    const baseStart = base.startDate ? this.getTimestamp(base.startDate) : -Infinity;
    const baseEnd = base.endDate ? this.getTimestamp(base.endDate) : Infinity;

    const targetStart = target.startDate ? this.getTimestamp(target.startDate) : -Infinity;
    const targetEnd = target.endDate ? this.getTimestamp(target.endDate) : Infinity;

    return targetEnd < baseStart || baseEnd < targetStart;
  }

  public getRangesOutside(
    base: IDateRange,
    target: IDateRange,
    outputFormat: DayJSDateFormat = DayJSDateFormat.YYYYMMDD
  ): IDateRange[] {
    const baseStart = base.startDate ? this.getTimestamp(base.startDate) : -Infinity;
    const baseEnd = base.endDate ? this.getTimestamp(base.endDate) : Infinity;

    const targetStart = target.startDate ? this.getTimestamp(target.startDate) : -Infinity;
    const targetEnd = target.endDate ? this.getTimestamp(target.endDate) : Infinity;

    const outsideRanges: IDateRange[] = [];

    if (targetStart < baseStart) {
      outsideRanges.push(this.formatResultRange(targetStart, baseStart, outputFormat));
    }

    if (baseEnd < targetEnd) {
      outsideRanges.push(this.formatResultRange(baseEnd, targetEnd, outputFormat));
    }

    return outsideRanges.length > 0 ? outsideRanges : [];
  }

  public getCombinedRange(
    base: IDateRange,
    target: IDateRange,
    outputFormat: DayJSDateFormat = DayJSDateFormat.YYYYMMDD
  ): IDateRange {
    const baseStart = base.startDate ? this.getTimestamp(base.startDate) : -Infinity;
    const baseEnd = base.endDate ? this.getTimestamp(base.endDate) : Infinity;

    const targetStart = target.startDate ? this.getTimestamp(target.startDate) : -Infinity;
    const targetEnd = target.endDate ? this.getTimestamp(target.endDate) : Infinity;

    const start = Math.min(baseStart, targetStart);
    const end: number = Math.max(baseEnd, targetEnd);

    return this.formatResultRange(start, end, outputFormat);
  }

  public getMonthsInRange(
    endDate: string | null,
    months: number | null,
    outputFormat: DayJSDateFormat = DayJSDateFormat.YYYYMMDD
  ): IDateRange {
    const endDateJs = this.dayjsLocale.dayjs(endDate ?? undefined).startOf('day');
    const endTs = endDateJs.valueOf();
    let startTs: number | null = null;

    if (months !== null) {
      const startDayjs = endDateJs.subtract(months, 'month').startOf('day');
      startTs = startDayjs.valueOf();
    }

    return this.formatResultRange(startTs, endTs, outputFormat);
  }

  public formatResultRange(
    startTs: number | null,
    endTs: number | null,
    format: DayJSDateFormat = DayJSDateFormat.YYYYMMDD
  ): IDateRange {
    const isExistStartDate = startTs !== null && startTs !== -Infinity;
    const isExistEndDate = endTs !== null && endTs !== Infinity;

    return {
      ...(isExistStartDate ? { startDate: this.dayjsLocale.dayjs(startTs).format(format) } : {}),
      ...(isExistEndDate ? { endDate: this.dayjsLocale.dayjs(endTs).format(format) } : {}),
    };
  }
}
